import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AccesRefuse from "@/pages/AccesRefuse";
import Dashboard from "@/pages/Dashboard";

const Administration = lazy(() => import("@/pages/Administration"));
const VueGlobale = lazy(() => import("@/pages/VueGlobale"));
const Planning = lazy(() => import("@/pages/Planning"));
const OrdresList = lazy(() => import("@/pages/OrdresList"));
const OrdreDetail = lazy(() => import("@/pages/OrdreDetail"));
const VentesList = lazy(() => import("@/pages/VentesList"));
const VenteDetail = lazy(() => import("@/pages/VenteDetail"));
const PipelineOpportunites = lazy(() => import("@/pages/PipelineOpportunites"));
const OpportuniteDetail = lazy(() => import("@/pages/OpportuniteDetail"));
const Agenda = lazy(() => import("@/pages/Agenda"));
const FacturationList = lazy(() => import("@/pages/FacturationList"));
const FactureDetail = lazy(() => import("@/pages/FactureDetail"));
const StockPieces = lazy(() => import("@/pages/StockPieces"));
const PieceDetail = lazy(() => import("@/pages/PieceDetail"));
const StockVehicules = lazy(() => import("@/pages/StockVehicules"));
const VehiculeDetail = lazy(() => import("@/pages/VehiculeDetail"));
const Clients = lazy(() => import("@/pages/Clients"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const Portefeuille = lazy(() => import("@/pages/Portefeuille"));
const DossiersAdministratifs = lazy(() => import("@/pages/DossiersAdministratifs"));
const FicheVehicule = lazy(() => import("@/pages/FicheVehicule"));
const Parametres = lazy(() => import("@/pages/Parametres"));
const PiloteStock = lazy(() => import("@/pages/PiloteStock"));
const PlansActionReseau = lazy(() => import("@/pages/PlansActionReseau"));

const Loader = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-[14px] text-[--idc-muted]" data-testid="route-loader">Chargement…</div>
);

function RequireAuth() {
  const { auth } = useAuth();
  if (auth === undefined)
    return <div className="min-h-screen flex items-center justify-center bg-[#F6F7F9] text-[14px] text-[--idc-muted]">Chargement…</div>;
  if (!auth) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function Guard({ module, children }) {
  const { peutVoir } = useAuth();
  if (!peutVoir(module)) return <AccesRefuse />;
  return children;
}

function GuardManager({ children }) {
  const { auth } = useAuth();
  if (!["chef_ventes", "directeur", "dg", "admin"].includes(auth?.user?.role)) return <AccesRefuse />;
  return children;
}

function GuardAdmin({ children }) {
  const { auth } = useAuth();
  if (auth?.user?.role !== "admin") return <AccesRefuse />;
  return children;
}

function GuardSecretaire({ children }) {
  const { auth } = useAuth();
  if (!["secretaire_commerciale", "admin"].includes(auth?.user?.role)) return <AccesRefuse />;
  return children;
}

function GuardPar({ children }) {
  const { auth } = useAuth();
  if (!["vendeur", "chef_ventes", "directeur", "dg", "admin"].includes(auth?.user?.role)) return <AccesRefuse />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/vue-globale" element={<GuardManager><VueGlobale /></GuardManager>} />
            <Route path="/acces-refuse" element={<AccesRefuse />} />
            <Route path="/administration" element={<GuardAdmin><Administration /></GuardAdmin>} />
            <Route path="/planning" element={<Guard module="planning"><Planning /></Guard>} />
            <Route path="/ordres" element={<Guard module="ordres"><OrdresList /></Guard>} />
            <Route path="/ordres/:id" element={<Guard module="ordres"><OrdreDetail /></Guard>} />
            <Route path="/ventes" element={<Guard module="ventes"><VentesList /></Guard>} />
            <Route path="/ventes/:id" element={<Guard module="ventes"><VenteDetail /></Guard>} />
            <Route path="/dossiers-administratifs" element={<GuardSecretaire><DossiersAdministratifs /></GuardSecretaire>} />
            <Route path="/pipeline" element={<Guard module="pipeline"><PipelineOpportunites /></Guard>} />
            <Route path="/pipeline/opportunites/:id" element={<Guard module="pipeline"><OpportuniteDetail /></Guard>} />
            <Route path="/plans-action-reseau" element={<GuardPar><PlansActionReseau /></GuardPar>} />
            <Route path="/agenda" element={<Guard module="agenda"><Agenda /></Guard>} />
            <Route path="/facturation" element={<Guard module="facturation"><FacturationList /></Guard>} />
            <Route path="/facturation/:id" element={<Guard module="facturation"><FactureDetail /></Guard>} />
            <Route path="/stock-pieces" element={<Guard module="stock_pieces"><StockPieces /></Guard>} />
            <Route path="/stock-pieces/:id" element={<Guard module="stock_pieces"><PieceDetail /></Guard>} />
            <Route path="/stock-vehicules" element={<Guard module="stock_vehicules"><StockVehicules /></Guard>} />
            <Route path="/pilote-stock" element={<Guard module="stock_vehicules"><PiloteStock /></Guard>} />
            <Route path="/stock-vehicules/:id" element={<Guard module="stock_vehicules"><VehiculeDetail /></Guard>} />
            <Route path="/clients" element={<Guard module="clients"><Clients /></Guard>} />
            <Route path="/clients/:id" element={<Guard module="clients"><ClientDetail /></Guard>} />
            <Route path="/portefeuille" element={<Guard module="clients"><Portefeuille /></Guard>} />
            <Route path="/vehicules/:id" element={<Guard module="clients"><FicheVehicule /></Guard>} />
            <Route path="/parametres" element={<Guard module="parametres"><Parametres /></Guard>} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
